import React, { useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Tab, Tabs, Typography } from '@mui/material';
import { Icon } from "@iconify/react";
import GenericDataGrid from '../../components/genericTable';
import { AntSwitch } from '../../components/antSwitch';
import { useNavigate } from 'react-router-dom';
import { clearFinancialSupportWizard, readFinancialSupportPrograms } from './financialSupportProgramWizardStorage';
import { getFinancialSupportProgramsCatalog } from './financialSupportProgramsSeed';
import { createManagementTableSx } from '../../styles/tableStyles';
import { managementCreateButtonSx, managementHeaderActionSx, managementHeaderContentSx, managementHeaderSx, managementPageSubtitleSx, managementPageTitleSx } from '../../styles/managementStyles';
import { tableApproveButtonSx, tableDeleteButtonSx, tableRejectButtonSx, tableViewButtonSx } from '../../styles/actionStyles';
import StatusChip from '../../components/statusChip';
import { buttonContentSx } from '../../styles/dialogStyles';
import { mediumMaterialIconSx } from '../../styles/iconStyles';
import { FilterSelect } from '../../components/filterControls';
import { filterBarSx } from '../../styles/filterStyles';

const financialSupportGridSx = createManagementTableSx({
  headerFontSize: 18,
  cellPaddingX: 10,
});

const detailCardSx = {
  border: '1px solid #DFE5F6',
  borderRadius: '16px',
  backgroundColor: '#FFFFFF',
  p: 2.5,
  height: '100%',
};

const applicationsSeed = [
  {
    id: 101,
    applicantName: 'Riya Sharma',
    email: 'riya.sharma@example.com',
    programName: 'Agricultural Credit',
    provider: 'National Bank',
    region: 'India',
    status: 'PENDING',
    submittedAt: '1/9/2026',
    requestedAmount: '$2,500',
    planName: 'Growth Support',
    firstName: 'Riya',
    lastName: 'Sharma',
    userTypeName: 'Farmer',
    countryName: 'India',
    countryCode: '+91',
    phoneNumber: '9876543210',
    address: 'Bhopal, Madhya Pradesh',
    businessName: 'Sharma Agri Produce',
    farmSize: '4 acres',
    annualIncome: '$7,200',
    applicationReason: 'Seasonal financing for seed, irrigation and equipment upgrades.',
    submittedDocuments: 'National ID, bank statement, land ownership proof',
  },
  {
    id: 102,
    applicantName: 'David Mwangi',
    email: 'david.mwangi@example.com',
    programName: 'Agri Input Subsidy',
    provider: 'Rural Cooperative',
    region: 'Kenya',
    status: 'APPROVED',
    submittedAt: '1/7/2026',
    requestedAmount: '$1,200',
    planName: 'Basic Support',
    firstName: 'David',
    lastName: 'Mwangi',
    userTypeName: 'Distributor',
    countryName: 'Kenya',
    countryCode: '+254',
    phoneNumber: '712345678',
    address: 'Nakuru County',
    businessName: 'Mwangi Farm Inputs',
    farmSize: 'N/A',
    annualIncome: '$12,000',
    applicationReason: 'Financing to support regional seed distribution.',
    submittedDocuments: 'Business registration, tax certificate',
  },
  {
    id: 103,
    applicantName: 'Amina Yusuf',
    email: 'amina.yusuf@example.com',
    programName: 'Women Enterprise Grant',
    provider: 'Development Fund',
    region: 'Nigeria',
    status: 'REJECTED',
    submittedAt: '1/5/2026',
    requestedAmount: '$3,000',
    planName: 'Enterprise Support',
    firstName: 'Amina',
    lastName: 'Yusuf',
    userTypeName: 'Entrepreneur',
    countryName: 'Nigeria',
    countryCode: '+234',
    phoneNumber: '8034567890',
    address: 'Kaduna',
    businessName: 'Amina Agro Foods',
    farmSize: 'N/A',
    annualIncome: '$9,500',
    applicationReason: 'Expansion capital for food processing equipment.',
    submittedDocuments: 'Bank statement, CAC certificate',
  },
];

function DetailField({ label, value }) {
  return (
    <Box sx={detailCardSx}>
      <Typography sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', fontWeight: 500, color: '#1C1B1F', mb: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
        {value || '-'}
      </Typography>
    </Box>
  );
}

function FinancialSupport() {
  const navigate = useNavigate();
  const persistedPrograms = readFinancialSupportPrograms();
  const programsRows = useMemo(() => getFinancialSupportProgramsCatalog(persistedPrograms), [persistedPrograms]);
  const [activeTab, setActiveTab] = useState('programs');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({
    type: '',
    region: '',
    status: '',
    provider: '',
  });
  const [applicationsRows, setApplicationsRows] = useState(applicationsSeed);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const filterOptions = useMemo(() => ({
    type: Array.from(new Set(programsRows.map((row) => row.type).filter(Boolean))),
    region: Array.from(new Set(programsRows.map((row) => row.region).filter(Boolean))),
    status: Array.from(new Set(programsRows.map((row) => row.status).filter(Boolean))),
    provider: Array.from(new Set(programsRows.map((row) => row.provider).filter(Boolean))),
  }), [programsRows]);

  const filteredProgramRows = useMemo(() => (
    programsRows.filter((row) => {
      const typeMatch = !filters.type || row.type === filters.type;
      const regionMatch = !filters.region || row.region === filters.region;
      const statusMatch = !filters.status || row.status === filters.status;
      const providerMatch = !filters.provider || row.provider === filters.provider;
      return typeMatch && regionMatch && statusMatch && providerMatch;
    })
  ), [filters, programsRows]);

  const activeFilteredRows = activeTab === 'programs' ? filteredProgramRows : applicationsRows;

  const pagedRows = useMemo(() => {
    const start = page * pageSize;
    return activeFilteredRows.slice(start, start + pageSize);
  }, [activeFilteredRows, page, pageSize]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleApplicationStatus = (id, status) => {
    setApplicationsRows((prev) => prev.map((row) => (
      row.id === id ? { ...row, status } : row
    )));
    if (selectedApplication?.id === id) {
      setSelectedApplication((prev) => ({ ...prev, status }));
    }
  };

  const programColumns = [
    {
      field: 'programName',
      headerName: 'Program Name',
      flex: 1.3,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.9,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'provider',
      headerName: 'Provider',
      flex: 1.2,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'region',
      headerName: 'Region',
      flex: 0.9,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1.1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
          <AntSwitch checked={params.row.status === 'ACTIVE'} inputProps={{ 'aria-label': 'financial support status' }} />
          <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', fontWeight: 500, color: '#4F8506' }}>
            {params.row.status === 'ACTIVE' ? 'Active' : 'Inactive'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'validity',
      headerName: 'Validity',
      flex: 1.2,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      flex: 1.1,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 0.9,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
          {/* Icon-only, so it needs a name of its own: without one it announces
              as "visibility" — the ligature text — to a screen reader, and is
              the only View action on the platform that cannot be found by name. */}
          <Button aria-label="View" onClick={() => navigate(`/financialSupport/view/${params.row.id}`)} sx={{ ...tableViewButtonSx, minWidth: 64 }}>
            <Box component="span" className="material-symbols-rounded" sx={mediumMaterialIconSx}>visibility</Box>
          </Button>
          <Button sx={{ ...tableDeleteButtonSx, minWidth: 64 }}>
            <Box component="span" className="material-symbols-rounded" sx={mediumMaterialIconSx}>delete</Box>
          </Button>
        </Box>
      ),
    },
  ];

  const applicationColumns = [
    {
      field: 'applicantName',
      headerName: 'Applicant',
      flex: 1.15,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.3,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'programName',
      headerName: 'Program',
      flex: 1.15,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'submittedAt',
      headerName: 'Submitted',
      flex: 0.95,
      sortable: false,
      renderCell: (params) => <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>{params.value || '-'}</Typography>,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.95,
      sortable: false,
      renderCell: (params) => <StatusChip status={params.value} size="compact" />,
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 1.9,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Button
            onClick={() => setSelectedApplication(params.row)}
            startIcon={<span className="material-symbols-rounded">visibility</span>}
            sx={tableViewButtonSx}
          >
            View
          </Button>
          <Button
            onClick={() => handleApplicationStatus(params.row.id, 'APPROVED')}
            disabled={params.row.status === 'APPROVED'}
            startIcon={<span className="material-symbols-rounded">check</span>}
            sx={tableApproveButtonSx}
          >
            Approve
          </Button>
          <Button
            onClick={() => handleApplicationStatus(params.row.id, 'REJECTED')}
            disabled={params.row.status === 'REJECTED'}
            startIcon={<span className="material-symbols-rounded">close</span>}
            sx={tableRejectButtonSx}
          >
            Reject
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: 370 }, borderBottom: '1px solid #CDD6EA', mb: { xs: 4, md: 5 } }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            setActiveTab(value);
            setPage(0);
            setFilters({ type: '', region: '', status: '', provider: '' });
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            '& .MuiTabs-flexContainer': {
              gap: { xs: 3, sm: 4 },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 999,
              backgroundColor: '#4F8506',
            },
          }}
        >
          <Tab label="Programs" value="programs" sx={{ minHeight: 48, px: 0, textTransform: 'none', fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', fontWeight: 500, color: '#4A4459', '&.Mui-selected': { color: '#4F8506' } }} />
          <Tab label="Applications" value="applications" sx={{ minHeight: 48, px: 0, textTransform: 'none', fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', fontWeight: 500, color: '#4A4459', '&.Mui-selected': { color: '#4F8506' } }} />
        </Tabs>
      </Box>

      <Box sx={managementHeaderSx}>
        <Box sx={managementHeaderContentSx}>
          <Typography className="app-page-title" sx={managementPageTitleSx}>
            {activeTab === 'programs' ? 'Financial Support Programs' : 'Financial Support Applications'}
          </Typography>
          <Typography sx={managementPageSubtitleSx}>
            {activeTab === 'programs'
              ? 'Create and manage financial support programs for your subscriptions.'
              : 'Review submitted financial support applications together with the applicant signup information.'}
          </Typography>
        </Box>
        {activeTab === 'programs' && (
          <Box sx={managementHeaderActionSx}>
            <Button
              onClick={() => {
                clearFinancialSupportWizard();
                navigate('/financialSupport/create-program');
              }}
              variant="contained"
              sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 220 } }}
            >
              <Box component="span" sx={buttonContentSx}>
                <Icon icon="tabler:plus" width="18" height="18" />
                <span>CREATE PROGRAM</span>
              </Box>
            </Button>
          </Box>
        )}
      </Box>

      {activeTab === 'programs' && (
        <Box sx={{ ...filterBarSx, mb: 3 }}>
          <FilterSelect value={filters.type} onChange={(value) => handleFilterChange('type', value)} placeholder="All Types" options={filterOptions.type} testId="fs-type-filter" />
          <FilterSelect value={filters.region} onChange={(value) => handleFilterChange('region', value)} placeholder="All Regions" options={filterOptions.region} testId="fs-region-filter" />
          <FilterSelect value={filters.status} onChange={(value) => handleFilterChange('status', value)} placeholder="All Status" options={filterOptions.status} testId="fs-status-filter" />
          <FilterSelect value={filters.provider} onChange={(value) => handleFilterChange('provider', value)} placeholder="All Providers" options={filterOptions.provider} testId="fs-provider-filter" />
        </Box>
      )}

      <Box sx={{ width: '100%', padding: '0 0px' }}>
        <GenericDataGrid
          rows={pagedRows}
          columns={activeTab === 'programs' ? programColumns : applicationColumns}
          rowCount={activeFilteredRows.length}
          page={page}
          pageSize={pageSize}
          loading={false}
          errors=""
          checkbox='false'
          dataGridSx={financialSupportGridSx}
          tableMinWidth={activeTab === 'programs' ? 1220 : 1360}
          rowHeight={60}
          onPaginationChange={(nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          }}
        />
      </Box>

      <Dialog
        open={!!selectedApplication}
        onClose={() => setSelectedApplication(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            border: '1px solid #DFE5F6',
            boxShadow: '0px 16px 40px rgba(31, 46, 85, 0.14)',
            mx: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ px: { xs: 2.5, sm: 4 }, pt: { xs: 2.5, sm: 4 }, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography className="app-page-title" sx={{ fontSize: { xs: 28, sm: 30, md: 34 }, lineHeight: { xs: '34px', sm: '36px', md: '40px' }, letterSpacing: '-0.25px', fontWeight: 700, color: '#1C1B1F', mb: 1 }}>
                Application Details
              </Typography>
              <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
                Review signup information together with the submitted financial support request.
              </Typography>
            </Box>
            <Button
              onClick={() => setSelectedApplication(null)}
              sx={{ minWidth: 40, width: 40, height: 40, borderRadius: '50%', color: '#1C1B1F' }}
            >
              <span className="material-symbols-rounded">close</span>
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, pb: { xs: 2.5, sm: 4 } }}>
          {selectedApplication && (
            <Box>
              <Typography sx={{ fontSize: 18, lineHeight: '28px', fontWeight: 600, color: '#1C1B1F', mb: 2 }}>
                Applicant Information
              </Typography>
              <Grid container spacing={2.5} mb={4}>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="First Name" value={selectedApplication.firstName} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Last Name" value={selectedApplication.lastName} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Email" value={selectedApplication.email} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Phone Number" value={`${selectedApplication.countryCode} ${selectedApplication.phoneNumber}`} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="User Type" value={selectedApplication.userTypeName} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Country" value={selectedApplication.countryName} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Address" value={selectedApplication.address} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Business Name" value={selectedApplication.businessName} /></Grid>
              </Grid>

              <Typography sx={{ fontSize: 18, lineHeight: '28px', fontWeight: 600, color: '#1C1B1F', mb: 2 }}>
                Financial Support Application
              </Typography>
              <Grid container spacing={2.5} mb={4}>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Program" value={selectedApplication.programName} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Provider" value={selectedApplication.provider} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Plan" value={selectedApplication.planName} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Requested Amount" value={selectedApplication.requestedAmount} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Submitted On" value={selectedApplication.submittedAt} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Application Status" value={<StatusChip status={selectedApplication.status} />} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Annual Income" value={selectedApplication.annualIncome} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><DetailField label="Farm / Business Size" value={selectedApplication.farmSize} /></Grid>
                <Grid size={12}><DetailField label="Reason for Applying" value={selectedApplication.applicationReason} /></Grid>
                <Grid size={12}><DetailField label="Submitted Documents" value={selectedApplication.submittedDocuments} /></Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
                <Button
                  onClick={() => handleApplicationStatus(selectedApplication.id, 'REJECTED')}
                  sx={{ ...tableRejectButtonSx, minWidth: 110, height: 40 }}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => handleApplicationStatus(selectedApplication.id, 'APPROVED')}
                  sx={{
                    ...tableApproveButtonSx,
                    minWidth: 110,
                    height: 40,
                    bgcolor: '#4F8506',
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#4F8506', boxShadow: 'none' },
                    '&.Mui-disabled': { bgcolor: '#9CB67B', color: '#FFFFFF' },
                  }}
                >
                  Approve
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default FinancialSupport;
